import { format } from 'date-fns'
import { getTimezoneOffsetInHours } from 'www/utils'
import { apiScheduleTelemed } from 'www/api'
import {
  getDoctorRequestInterface,
  getDoctorOnlineRequestInterface,
} from 'www/modules/Schedule/models'
import type {
  DoctorLpuRequestItem,
  DoctorRequestInterface,
  DoctorOnlineRequestInterface,
  DoctorOnlineLpuParam,
} from 'www/modules/Schedule/models/request'
import type {
  getDoctorsStorageInterface,
  getDoctorsLpuStorageInterface,
} from 'www/modules/Schedule/models/storage'

type DoctorsStorage =
  | ReturnType<typeof getDoctorsStorageInterface>
  | ReturnType<typeof getDoctorsLpuStorageInterface>

type DoctorsScheduleData = {
  storage: DoctorsStorage
}

/**
 * @description
 * Собирает данные для запроса за расписанием для врачей
 *
 * @param opts.data - данные 'ScheduleData'
 * @param opts.doctorsLpusPart - список с [{ doctor_fio, doctor_id, lpu_id }], аналогичный data.storage.doctorsLpus, просто собран вручную
 * Используется на странице списка врачей клиники для запросов расписания по частям. Если это поле передано - у него приоритет перед data.storage.doctorsLpus
 *
 * @returns объект в формате 'getDoctorRequestInterface' | 'getDoctorOnlineRequestInterface'
 */

const getDoctorsRequestData = ({
  data,
  doctorsLpusPart = [],
}: {
  data: DoctorsScheduleData
  doctorsLpusPart?: DoctorLpuRequestItem[]
}): DoctorRequestInterface | DoctorOnlineRequestInterface => {
  const {
    dateStart,
    timedelta,
    doctorsLpus,
  } = data.storage

  const isDoctorListOnline = 'isDoctorListOnline' in data.storage
    ? data.storage.isDoctorListOnline
    : null
  const isFilterByPlaceOnline = 'isFilterByPlaceOnline' in data.storage
    ? data.storage.isFilterByPlaceOnline
    : null

  const isTelemed = Boolean(isDoctorListOnline || isFilterByPlaceOnline)
  const baseData = isTelemed ? getDoctorOnlineRequestInterface() : getDoctorRequestInterface()
  const doctorsHasSlots = doctorsLpusPart.length
    ? doctorsLpusPart
    : doctorsLpus.filter(item => 'has_slots' in item && item.has_slots)

  if (!isTelemed) {
    (baseData as DoctorRequestInterface).doctors_lpus = doctorsHasSlots
  }

  if (isTelemed) {
    const telemedData = baseData as DoctorOnlineRequestInterface

    telemedData.user_start_date = format(new Date(), 'yyyy-MM-dd')
    telemedData.user_timezone = getTimezoneOffsetInHours()

    const lpuParamsObject: Record<number, DoctorOnlineLpuParam> = {}

    doctorsHasSlots.forEach(item => {
      const {
        lpu_id: lpuId,
        doctor_id: doctorId,
      } = item
      const telemedPrice = 'telemed_price' in item ? item.telemed_price : null
      const telemedMedtochkaPrice = 'telemed_medtochka_price' in item
        ? item.telemed_medtochka_price
        : null

      const scheduleParam = {
        doctor_id: doctorId,
        type_schedule: apiScheduleTelemed.getScheduleTypeV2({
          telemed: telemedPrice,
          telemedMedtochka: telemedMedtochkaPrice,
        }),
      }

      if (lpuId in lpuParamsObject) {
        lpuParamsObject[lpuId].schedule_params.push(scheduleParam)
      } else {
        lpuParamsObject[lpuId] = {
          lpu_id: lpuId,
          schedule_params: [scheduleParam],
        }
      }
    })

    telemedData.lpu_params = Object.values(lpuParamsObject)
  } else {
    const doctorData = baseData as DoctorRequestInterface

    doctorData.dt_start = dateStart
    doctorData.town_timedelta = timedelta
  }

  return baseData
}

export default getDoctorsRequestData
